/**
 * ClientManagementModal Component
 * Modal for adding client/cubierto data with comprehensive form validation
 */

import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import { Icon } from '../ui/Icon';
import toast from 'react-hot-toast';

import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { FormField } from '../forms/FormField';
import { SelectField } from '../forms/SelectField';
import { LoadingSpinner } from '../ui/LoadingSpinner';

import { ClientesForm, PERIOD_TYPE_OPTIONS } from '../../types/forms';
import { ClientEntry } from '../../types/api';
import { entrySchemas } from '../forms/validation';
import { apiService } from '../../services/api';
import { formatDateForInput } from '../../utils/formatters';

// ============================================================================
// Styled Components
// ============================================================================

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0; /* Prevent flex item overflow */
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border);
  padding-bottom: 0.5rem;
`;

const InfoBox = styled.div`
  background: var(--brand-50);
  border: 1px solid var(--brand-200);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--brand-700);
  line-height: 1.4;
`;

const ErrorMessage = styled.div`
  background: var(--danger-50);
  border: 1px solid var(--danger-100);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: var(--danger-600);
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  background: var(--success-50);
  border: 1px solid var(--success-100);
  border-radius: 8px;
  padding: 0.75rem;
  margin-bottom: 1rem;
  color: var(--success-600);
  font-size: 0.875rem;
`;

// ============================================================================
// Component Interface
// ============================================================================

export interface ClientManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (clientEntry: ClientEntry) => void;
  initialData?: Partial<ClientesForm>;
}

// ============================================================================
// Helper Functions
// ============================================================================

const getTodayDate = (): string => {
  return formatDateForInput(new Date());
};

// ============================================================================
// ClientManagementModal Component
// ============================================================================

export const ClientManagementModal: React.FC<ClientManagementModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);

  // Form setup with react-hook-form and yup validation
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    reset,
    watch,
  } = useForm<ClientesForm>({
    resolver: yupResolver(entrySchemas.clientesEntry) as any,
    defaultValues: {
      fecha: initialData?.fecha || getTodayDate(),
      periodo: initialData?.periodo || ('diario' as const),
      total_clientes: initialData?.total_clientes || 0,
      total_cubiertos: initialData?.total_cubiertos || 0,
      notas: initialData?.notas || '',
    },
    mode: 'onChange',
  });

  // Watch form values for dynamic feedback
  const watchedValues = watch();

  // Handle form submission
  const onSubmit = async (data: ClientesForm) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      console.log('📤 [ClientManagementModal] Submitting client data:', data);

      // Call API to log client entry
      const response = await apiService.logClientEntry(data);

      console.log(
        '✅ [ClientManagementModal] Client entry logged successfully:',
        response,
      );

      setSubmitSuccess('Datos de clientes registrados exitosamente');

      // Show success toast
      toast.success('Datos de clientes registrados correctamente');

      // Call success callback if provided
      if (onSuccess && response) {
        onSuccess(response);
      }

      // Reset form and close modal after short delay
      setTimeout(() => {
        reset();
        onClose();
        setSubmitSuccess(null);
      }, 1500);
    } catch (error) {
      console.error(
        '❌ [ClientManagementModal] Error logging client entry:',
        error,
      );

      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Error al registrar los datos de clientes';

      setSubmitError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (isSubmitting) return; // Prevent closing while submitting

    reset();
    setSubmitError(null);
    setSubmitSuccess(null);
    onClose();
  };

  // Handle form reset
  const handleReset = () => {
    reset({
      fecha: getTodayDate(),
      periodo: 'diario',
      total_clientes: 0,
      total_cubiertos: 0,
      notas: '',
    });
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  // Calculate ratio for validation feedback
  const clientToCubiertoRatio =
    watchedValues.total_clientes > 0
      ? (watchedValues.total_cubiertos / watchedValues.total_clientes).toFixed(
          1,
        )
      : '0.0';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Registrar Datos de Clientes"
      description="Ingresa la información de clientes y cubiertos para el período seleccionado"
      size="md"
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      data-testid="client-management-modal"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="cancel-button"
          >
            Cancelar
          </Button>
          <Button
            variant="secondary"
            onClick={handleReset}
            disabled={isSubmitting || !isDirty}
            data-testid="reset-button"
          >
            Limpiar
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || isSubmitting}
            data-testid="submit-button"
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="sm" />
                Guardando...
              </>
            ) : (
              'Guardar Datos'
            )}
          </Button>
        </>
      }
    >
      <FormContainer>
        {/* Info box */}
        <InfoBox>
          <InfoText>
            <Icon name="info" size="sm" aria-hidden /> <strong>Tip:</strong> Los
            datos de clientes te ayudan a calcular métricas de sostenibilidad
            por cubierto servido. Un ratio típico es de 1.2-1.5 cubiertos por
            cliente.
          </InfoText>
        </InfoBox>

        {/* Error message */}
        {submitError && (
          <ErrorMessage role="alert">
            <Icon name="error" size="sm" aria-hidden /> {submitError}
          </ErrorMessage>
        )}

        {/* Success message */}
        {submitSuccess && (
          <SuccessMessage role="alert">
            <Icon name="success" size="sm" aria-hidden /> {submitSuccess}
          </SuccessMessage>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Date and Period Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="calendar" size="sm" aria-hidden /> Período de Registro
            </SectionTitle>

            <FormRow>
              <Controller
                name="fecha"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label="Fecha"
                    type="date"
                    required
                    error={fieldState.error}
                    helperText="Fecha del registro de datos"
                    max={getTodayDate()}
                    data-testid="fecha-field"
                  />
                )}
              />

              <Controller
                name="periodo"
                control={control}
                render={({ field, fieldState }) => (
                  <SelectField
                    {...field}
                    label="Tipo de Período"
                    options={PERIOD_TYPE_OPTIONS}
                    required
                    error={fieldState.error}
                    helperText="Frecuencia del registro"
                    data-testid="periodo-field"
                  />
                )}
              />
            </FormRow>
          </FormSection>

          {/* Client Data Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="users" size="sm" aria-hidden /> Datos de Clientes
            </SectionTitle>

            <FormRow>
              <Controller
                name="total_clientes"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label="Total de Clientes"
                    type="number"
                    required
                    min={0}
                    step={1}
                    error={fieldState.error}
                    helperText="Número de clientes únicos"
                    startAdornment={
                      <Icon name="profile" size="xs" aria-hidden />
                    }
                    data-testid="total-clientes-field"
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />

              <Controller
                name="total_cubiertos"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField
                    {...field}
                    label="Total de Cubiertos"
                    type="number"
                    required
                    min={0}
                    step={1}
                    error={fieldState.error}
                    helperText={`Servicios totales (ratio: ${clientToCubiertoRatio})`}
                    startAdornment={
                      <Icon name="clipboard" size="xs" aria-hidden />
                    }
                    data-testid="total-cubiertos-field"
                    onChange={(e) =>
                      field.onChange(parseInt(e.target.value) || 0)
                    }
                  />
                )}
              />
            </FormRow>

            {/* Ratio validation feedback */}
            {watchedValues.total_clientes > 0 &&
              watchedValues.total_cubiertos > 0 && (
                <InfoBox>
                  <InfoText>
                    <Icon name="chart" size="sm" aria-hidden />{' '}
                    <strong>Ratio actual:</strong> {clientToCubiertoRatio}{' '}
                    cubiertos por cliente
                    {parseFloat(clientToCubiertoRatio) < 1.0 && (
                      <span style={{ color: 'var(--warning-600)' }}>
                        {' '}
                        (<Icon name="warning" size="xs" aria-hidden /> Ratio
                        bajo - verifica los datos)
                      </span>
                    )}
                    {parseFloat(clientToCubiertoRatio) > 3.0 && (
                      <span style={{ color: 'var(--warning-600)' }}>
                        {' '}
                        (<Icon name="warning" size="xs" aria-hidden /> Ratio
                        alto - verifica los datos)
                      </span>
                    )}
                  </InfoText>
                </InfoBox>
              )}
          </FormSection>

          {/* Notes Section */}
          <FormSection>
            <SectionTitle>
              <Icon name="edit" size="sm" aria-hidden /> Notas Adicionales
            </SectionTitle>

            <Controller
              name="notas"
              control={control}
              render={({ field, fieldState }) => (
                <div>
                  <label
                    htmlFor="notas-field"
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                    }}
                  >
                    Notas
                  </label>
                  <textarea
                    {...field}
                    id="notas-field"
                    rows={3}
                    placeholder="Ej: Evento especial, promoción, día festivo..."
                    maxLength={500}
                    data-testid="notas-field"
                    style={{
                      width: '100%',
                      padding: '0.875rem 1rem',
                      border: `1px solid ${fieldState.error ? 'var(--danger-600)' : 'var(--border)'}`,
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontFamily: 'inherit',
                      color: 'var(--text-primary)',
                      backgroundColor: 'var(--surface-strong)',
                      resize: 'vertical',
                      minHeight: '80px',
                      transition:
                        'border-color 0.2s ease, box-shadow 0.2s ease',
                    }}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'var(--brand-600)';
                      e.target.style.boxShadow = '0 0 0 3px var(--brand-50)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = fieldState.error
                        ? 'var(--danger-600)'
                        : 'var(--border)';
                      e.target.style.boxShadow = 'none';
                    }}
                  />
                  {fieldState.error && (
                    <div
                      style={{
                        marginTop: '0.25rem',
                        fontSize: '0.75rem',
                        color: 'var(--danger-600)',
                      }}
                    >
                      {fieldState.error.message}
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: '0.25rem',
                      fontSize: '0.75rem',
                      color: 'var(--text-muted)',
                    }}
                  >
                    Información adicional sobre el registro (opcional)
                  </div>
                </div>
              )}
            />
          </FormSection>
        </form>
      </FormContainer>
    </Modal>
  );
};

export default ClientManagementModal;
